import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/apiClient';

// Using inline SVGs for icons to keep it a single file component
const GamepadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2 text-gray-400">
        <line x1="6" y1="12" x2="18" y2="12"></line>
        <line x1="12" y1="6" x2="12" y2="18"></line>
        <path d="M17.5 21a4.5 4.5 0 1 0-9 0 4.5 4.5 0 0 0 9 0Z"></path>
        <path d="M6.5 3A4.5 4.5 0 1 0 11 7.5 4.5 4.5 0 0 0 6.5 3Z"></path>
    </svg>
);

const ClockIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 mr-2 text-green-400">
        <circle cx="12" cy="12" r="10"></circle>
        <polyline points="12 6 12 12 16 14"></polyline>
    </svg>
);

const ShieldIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-blue-400">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    </svg>
);

const KeyCap = ({ children }) => (
    <span className="text-center font-semibold text-gray-900 bg-gray-300 rounded-sm w-5 h-5 inline-block leading-5 text-xs ml-auto">
        {children}
    </span>
);

const buttonBaseClasses = "w-[calc(100vw-1.5rem)] max-w-[220px] sm:w-64 py-2.5 sm:py-3 rounded-lg border border-white/30 bg-black/20 text-white/80 backdrop-blur-sm transition-all duration-300 ease-in-out focus:outline-none shadow-[0_0_5px_#0ff,0_0_15px_#0ff,0_0_25px_#0ff]";
const neonHoverEffect = "hover:text-white hover:border-cyan-400 hover:shadow-[0_0_5px_#f0f,0_0_15px_#f0f,0_0_25px_#f0f]";

// Helper function to format arena names
const formatArenaName = (arena) => {
    if (!arena) return '';
    return arena
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export default function AchievementPage() {
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    // State management
    const [activeTab, setActiveTab] = useState('global');
    const [achievements, setAchievements] = useState([]);
    const [leaderboard, setLeaderboard] = useState([]);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userRank, setUserRank] = useState(null);
    const [performanceMetrics, setPerformanceMetrics] = useState(null);

    // Fetch user profile data
    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                const response = await apiClient.get('/users/me');
                if (response.data && response.data.success) {
                    setUserProfile(response.data.data);
                } else {
                    // Fallback: try dashboard endpoint
                    const dashboardResponse = await apiClient.get('/users/me/dashboard');
                    if (dashboardResponse.data && dashboardResponse.data.stats) {
                        setUserProfile({
                            first_name: dashboardResponse.data.user?.firstName || currentUser?.first_name || 'Player',
                            last_name: dashboardResponse.data.user?.lastName || currentUser?.last_name || '',
                            level: dashboardResponse.data.stats.level || 1,
                            total_xp: dashboardResponse.data.stats.total_xp || 0,
                            xp: dashboardResponse.data.stats.xp || 0,
                        });
                    }
                }
            } catch (err) {
                console.error('Error fetching user profile:', err);
                // Set default values
                setUserProfile({
                    first_name: currentUser?.first_name || 'Player',
                    last_name: currentUser?.last_name || '',
                    level: 1,
                    total_xp: 0,
                    xp: 0,
                });
            }
        };

        fetchUserProfile();
    }, [currentUser]);

    // Fetch achievements and badges data
    useEffect(() => {
        const fetchAchievements = async () => {
            try {
                setLoading(true);
                setError(null);
                console.log('🔄 Fetching achievements for user:', currentUser?.id);
                const response = await apiClient.get('/games/achievements');

                if (response.data && response.data.success) {
                    console.log('✅ Achievements fetched:', response.data.data.achievements.length, 'items');
                    // Transform backend data to frontend format
                    const transformedAchievements = response.data.data.achievements.map(ach => ({
                        id: ach.id,
                        name: ach.title || ach.name,
                        description: ach.description,
                        category: ach.category,
                        cost: ach.xp_reward || 0,
                        earned: ach.earned || false,
                        awarded_on: ach.awarded_on,
                        icon: ach.icon,
                    }));

                    // Group by category and sort
                    const grouped = transformedAchievements.reduce((acc, ach) => {
                        const category = ach.category || 'Other';
                        if (!acc[category]) {
                            acc[category] = [];
                        }
                        acc[category].push(ach);
                        return acc;
                    }, {});

                    // Flatten and sort by earned status and cost
                    const sorted = Object.values(grouped).flat().sort((a, b) => {
                        if (a.earned !== b.earned) {
                            return b.earned ? 1 : -1; // Earned first
                        }
                        return b.cost - a.cost; // Higher cost first
                    });

                    setAchievements(sorted);

                    // Store performance metrics
                    if (response.data.data.performance_metrics) {
                        setPerformanceMetrics(response.data.data.performance_metrics);
                    }
                } else {
                    setError('Failed to load achievements');
                }
            } catch (err) {
                console.error('Error fetching achievements:', err);
                setError('Failed to load achievements. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        if (activeTab === 'individual') {
            setLoading(true);
            fetchAchievements();
        }
    }, [activeTab, currentUser?.id]); // Add currentUser.id as dependency to refresh when user changes

    // Fetch leaderboard data
    useEffect(() => {
        const fetchLeaderboard = async () => {
            try {
                setLoading(true);
                setError(null);
                const params = new URLSearchParams({
                    type: 'overall',
                    limit: '100',
                });

                const response = await apiClient.get(`/games/leaderboard?${params.toString()}`);

                if (response.data && response.data.success) {
                    const leaderboardData = response.data.data.leaderboard || [];
                    setLeaderboard(leaderboardData);

                    // Find user's rank
                    const rank = response.data.data.user_rank ||
                        leaderboardData.findIndex(entry => entry.user.id === currentUser?.id) + 1;
                    setUserRank(rank > 0 ? rank : null);
                } else {
                    setError('Failed to load leaderboard');
                }
            } catch (err) {
                console.error('Error fetching leaderboard:', err);
                setError('Failed to load leaderboard. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        if (activeTab === 'global') {
            fetchLeaderboard();
        }
    }, [activeTab, currentUser]);

    // Calculate player stats
    const playerStats = React.useMemo(() => {
        if (!userProfile) {
            return { rep: 0, level: 1, score: 0 };
        }

        const earnedCount = achievements.filter(a => a.earned).length;
        const rep = earnedCount; // REP = number of earned achievements
        const level = userProfile.level || 1;
        const score = userProfile.total_xp || userProfile.xp || 0;

        return { rep, level, score };
    }, [userProfile, achievements]);

    const playerName = userProfile
        ? `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() || 'Player'
        : currentUser
            ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || 'Player'
            : 'Player';

    return (
        <div className="min-h-screen bg-gray-900 text-gray-200 font-sans flex items-start lg:items-center justify-center p-3 sm:p-4 relative bg-cover bg-center" style={{ backgroundImage: "url('/assets/images/achievement_bg.gif')" }}>

            {/* Background Dim Overlay */}
            <div className="absolute inset-0 bg-black/60 z-0"></div>

            {/* Back to Main Page Button */}
            <button
                className={`fixed sm:absolute top-3 left-3 sm:top-auto sm:bottom-10 sm:left-6 z-20 ${buttonBaseClasses} ${neonHoverEffect}`}
                onClick={() => navigate('/')}
                aria-label="Back to main page"
            >
                <span className="text-sm sm:text-md font-semibold tracking-wide sm:tracking-wider whitespace-nowrap">
                    <span className="sm:hidden">Back to Main Page</span>
                    <span className="hidden sm:inline">Back to Main Page</span>
                </span>
            </button>

            <main className="w-full max-w-6xl mx-auto flex flex-col lg:flex-row items-stretch lg:items-center justify-center relative text-sm pt-16 sm:pt-0">

                {/* Left Section - Player Character */}
                <div className="w-full lg:w-1/2 flex flex-col items-center lg:items-start relative z-10 p-2 sm:p-4">
                    {/* Player Info Header */}
                    <div className="relative lg:absolute top-0 left-0 p-3 sm:p-4 mb-3 lg:mb-0 flex items-center space-x-4 bg-black/50 rounded-lg lg:rounded-br-lg">
                        <div className="flex items-center space-x-2">
                            <div>
                                <p className="font-bold text-white">{playerName}</p>
                                <div className="flex items-center text-xs text-gray-400 space-x-2">
                                    <span>REP {playerStats.rep}</span>
                                    <span>LVL {playerStats.level}</span>
                                    <span>SCR {playerStats.score}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Character Image Placeholder */}
                    <div className="flex-grow flex items-center justify-center w-full mt-2 lg:mt-0">
                        <img
                            src="/assets/images/characters/Shanaya4.png"
                            alt="Player Character"
                            className="max-w-2xl w-full h-auto max-h-[300px] sm:max-h-[420px] lg:max-h-none object-contain"
                        />
                    </div>
                </div>

                {/* Right Section - Achievements/Leaderboard */}
                <div className="w-full lg:w-1/2 mt-4 lg:mt-0 p-2 sm:p-4 relative z-10 lg:-ml-16">
                    <div className="bg-black/60 backdrop-blur-sm rounded-lg border border-gray-700">
                        {/* Tabs */}
                        <div className="flex border-b border-gray-700">
                            <button
                                onClick={() => setActiveTab('global')}
                                className={`flex-1 p-3 text-sm font-bold transition-colors ${activeTab === 'global'
                                    ? 'border-b-2 border-white text-white'
                                    : 'text-gray-500 hover:text-white'
                                    }`}
                            >
                                Global Standing
                            </button>
                            <button
                                onClick={() => setActiveTab('individual')}
                                className={`flex-1 p-3 text-sm font-bold transition-colors ${activeTab === 'individual'
                                    ? 'border-b-2 border-white text-white'
                                    : 'text-gray-500 hover:text-white'
                                    }`}
                            >
                                Individual Achievements
                            </button>
                        </div>

                        {/* Content Area */}
                        <div className="p-2 min-h-[320px] sm:min-h-[400px] max-h-[65vh] lg:max-h-[600px] overflow-y-auto">
                            {loading && (
                                <div className="flex items-center justify-center py-20">
                                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400"></div>
                                </div>
                            )}

                            {error && !loading && (
                                <div className="flex flex-col items-center justify-center py-20 text-center">
                                    <p className="text-red-400 mb-4">{error}</p>
                                    <button
                                        onClick={() => {
                                            setError(null);
                                            if (activeTab === 'individual') {
                                                // Retry achievements
                                                setActiveTab('global');
                                                setTimeout(() => setActiveTab('individual'), 100);
                                            } else {
                                                // Retry leaderboard
                                                setActiveTab('individual');
                                                setTimeout(() => setActiveTab('global'), 100);
                                            }
                                        }}
                                        className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 rounded-lg transition-colors"
                                    >
                                        Retry
                                    </button>
                                </div>
                            )}

                            {!loading && !error && activeTab === 'individual' && (
                                <>
                                    {achievements.length === 0 && (!performanceMetrics || !performanceMetrics.badges_count) ? (
                                        <div className="flex flex-col items-center justify-center py-20 text-center">
                                            <ShieldIcon />
                                            <p className="text-gray-400 mt-4">No achievements or badges available</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {/* Achievements Section */}
                                            {achievements.length > 0 && (
                                                <div>
                                                    <h3 className="text-sm font-bold text-cyan-400 mb-2 flex items-center">
                                                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                                            <path d="M12 2L9 9H2l7 5-3 8 6-5 6 5-3-8 7-5h-7L12 2z"></path>
                                                        </svg>
                                                        <span>Achievements ({achievements.filter(a => a.earned).length}/{achievements.length})</span>
                                                    </h3>
                                                    <ul className="space-y-2">
                                                        {achievements.map((ach) => (
                                                            <li
                                                                key={ach.id}
                                                                className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-md transition-all duration-200 ${ach.earned
                                                                    ? 'bg-white/10 border-l-4 border-cyan-400'
                                                                    : 'hover:bg-white/5'
                                                                    }`}
                                                            >
                                                                <div className="flex items-center flex-1 min-w-0">
                                                                    {ach.earned && (
                                                                        <svg className="w-5 h-5 text-yellow-400 mr-2 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                                                            <path d="M12 2L9 9H2l7 5-3 8 6-5 6 5-3-8 7-5h-7L12 2z"></path>
                                                                        </svg>
                                                                    )}
                                                                    <div className="min-w-0 flex-1">
                                                                        <span className={`block truncate ${ach.earned ? 'text-white font-semibold' : 'text-gray-300'}`}>
                                                                            {ach.name}
                                                                        </span>
                                                                        {ach.category && (
                                                                            <span className="text-xs text-gray-500">{ach.category}</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                {ach.cost > 0 && (
                                                                    <div className="flex items-center sm:ml-2 self-end sm:self-auto flex-shrink-0">
                                                                        <span className="mr-2 text-yellow-400 font-semibold">{ach.cost}</span>
                                                                        <svg className="w-4 h-4 text-yellow-500" viewBox="0 0 24 24" fill="currentColor">
                                                                            <path d="M12 2L9 9H2l7 5-3 8 6-5 6 5-3-8 7-5h-7L12 2z"></path>
                                                                        </svg>
                                                                    </div>
                                                                )}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            {/* Performance Insights Section */}
                                            {performanceMetrics && (
                                                <div>
                                                    <h3 className="text-sm font-bold text-cyan-400 mb-2 flex items-center">
                                                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
                                                        </svg>
                                                        <span>Performance Insights</span>
                                                    </h3>
                                                    <div className="space-y-2">
                                                        {/* Badges Count */}
                                                        {performanceMetrics.badges_count !== undefined && (
                                                            <div className="bg-white/5 border border-cyan-500/30 rounded-md p-3">
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-sm text-gray-300">Badges Earned</span>
                                                                    <span className="text-lg font-bold text-yellow-400">{performanceMetrics.badges_count}</span>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Max Points Arena */}
                                                        {performanceMetrics.max_points_arena && (
                                                            <div className="bg-white/5 border border-green-500/30 rounded-md p-3">
                                                                <div className="flex items-center justify-between mb-1">
                                                                    <span className="text-sm text-gray-300">Top Arena (Points)</span>
                                                                    <span className="text-xs text-green-400 font-semibold">{formatArenaName(performanceMetrics.max_points_arena.arena)}</span>
                                                                </div>
                                                                <div className="text-right">
                                                                    <span className="text-lg font-bold text-green-400">{performanceMetrics.max_points_arena.total_points.toLocaleString()}</span>
                                                                    <span className="text-xs text-gray-500 ml-1">XP</span>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Min Time Arena */}
                                                        {performanceMetrics.min_time_arena && (
                                                            <div className="bg-white/5 border border-blue-500/30 rounded-md p-3">
                                                                <div className="flex items-center justify-between mb-1">
                                                                    <span className="text-sm text-gray-300">Fastest Arena</span>
                                                                    <span className="text-xs text-blue-400 font-semibold">{formatArenaName(performanceMetrics.min_time_arena.arena)}</span>
                                                                </div>
                                                                <div className="text-right">
                                                                    <span className="text-lg font-bold text-blue-400">
                                                                        {Math.floor(performanceMetrics.min_time_arena.total_time / 60)}:{(performanceMetrics.min_time_arena.total_time % 60).toString().padStart(2, '0')}
                                                                    </span>
                                                                    <span className="text-xs text-gray-500 ml-1">({performanceMetrics.min_time_arena.challenges_completed} challenges)</span>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Average Learning Time */}
                                                        {performanceMetrics.avg_learning_time && performanceMetrics.avg_learning_time.total_completed > 0 && (
                                                            <div className="bg-white/5 border border-purple-500/30 rounded-md p-3">
                                                                <div className="flex items-center justify-between mb-1">
                                                                    <span className="text-sm text-gray-300">Avg Learning Time</span>
                                                                    <span className="text-xs text-gray-500">{performanceMetrics.avg_learning_time.total_completed} materials</span>
                                                                </div>
                                                                <div className="text-right">
                                                                    <span className="text-lg font-bold text-purple-400">
                                                                        {performanceMetrics.avg_learning_time.avg_time_minutes}
                                                                    </span>
                                                                    <span className="text-xs text-gray-500 ml-1">minutes</span>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Fastest Challenge */}
                                                        {performanceMetrics.fastest_challenge && (
                                                            <div className="bg-white/5 border border-yellow-500/30 rounded-md p-3">
                                                                <div className="flex items-center justify-between mb-1">
                                                                    <span className="text-sm text-gray-300">Fastest Challenge</span>
                                                                    <span className="text-xs text-yellow-400 font-semibold">{performanceMetrics.fastest_challenge.time_taken_formatted}</span>
                                                                </div>
                                                                <div className="text-xs text-gray-400 truncate">
                                                                    {performanceMetrics.fastest_challenge.title}
                                                                </div>
                                                                <div className="text-xs text-gray-500 mt-1">
                                                                    {formatArenaName(performanceMetrics.fastest_challenge.category)}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Strengths */}
                                                        {performanceMetrics.strengths && performanceMetrics.strengths.length > 0 && (
                                                            <div className="bg-white/5 border border-cyan-500/30 rounded-md p-3">
                                                                <div className="mb-2">
                                                                    <span className="text-sm font-bold text-cyan-400">Strengths</span>
                                                                </div>
                                                                <div className="space-y-1">
                                                                    {performanceMetrics.strengths.map((strength, idx) => (
                                                                        <div key={idx} className="flex items-center justify-between text-xs">
                                                                            <span className="text-gray-300 truncate flex-1 mr-2">
                                                                                {formatArenaName(strength.arena)}
                                                                            </span>
                                                                            <span className="text-cyan-400 font-semibold">
                                                                                {strength.avg_score}% ({strength.completions}/{strength.attempts})
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}

                            {!loading && !error && activeTab === 'global' && (
                                <>
                                    {leaderboard.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-20 text-center">
                                            <ShieldIcon />
                                            <p className="text-gray-400 mt-4">No leaderboard data available</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {userRank && (
                                                <div className="bg-cyan-500/20 border border-cyan-400 rounded-lg p-3 mb-4">
                                                    <p className="text-sm text-cyan-300">
                                                        Your Rank: <span className="font-bold text-white">#{userRank}</span>
                                                    </p>
                                                </div>
                                            )}
                                            <ul className="space-y-2">
                                                {leaderboard.slice(0, 20).map((entry, index) => {
                                                    const isCurrentUser = entry.user.id === currentUser?.id;
                                                    return (
                                                        <li
                                                            key={entry.user.id}
                                                            className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-md transition-all duration-200 ${isCurrentUser
                                                                ? 'bg-cyan-500/20 border-l-4 border-cyan-400'
                                                                : 'hover:bg-white/5'
                                                                }`}
                                                        >
                                                            <div className="flex items-center flex-1 min-w-0 w-full">
                                                                <span className={`text-sm font-bold mr-3 w-8 text-right ${isCurrentUser ? 'text-cyan-400' : 'text-gray-400'}`}>
                                                                    #{entry.rank}
                                                                </span>
                                                                <div className="flex items-center min-w-0 flex-1">
                                                                    {entry.user.initials && (
                                                                        <div className="w-8 h-8 rounded-full bg-cyan-500/30 flex items-center justify-center text-xs font-bold text-cyan-300 mr-2 flex-shrink-0">
                                                                            {entry.user.initials}
                                                                        </div>
                                                                    )}
                                                                    <div className="min-w-0 flex-1">
                                                                        <span className={`block truncate ${isCurrentUser ? 'text-cyan-300 font-semibold' : 'text-gray-300'}`}>
                                                                            {entry.user.name}
                                                                        </span>
                                                                        {entry.user.department && (
                                                                            <span className="text-xs text-gray-500">{entry.user.department}</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center ml-2 sm:ml-0 self-end sm:self-auto flex-shrink-0 bg-black/20 px-2 py-1 rounded">
                                                                <span className="text-yellow-400 font-semibold mr-2">
                                                                    {entry.stats.total_xp || entry.stats.weekly_xp || entry.stats.monthly_xp || 0}
                                                                </span>
                                                                <span className="text-xs text-gray-500">XP</span>
                                                            </div>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

            </main>
        </div>
    );
}